import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AgGridTable from '../../components/AgGridTable';
import SidebarPanel from '../../components/admin/SidebarPanel';
import EducationalTooltip from '../../components/shared/EducationalTooltip';
import RelationshipBadge from '../../components/shared/RelationshipBadge';
import ModelDocumentationModal from '../../components/shared/ModelDocumentationModal';
import SalesUserEditor from '../../components/sales/SalesUserEditor';
import AlertMessage from '../../components/shared/alertMessage';
import PasswordInput from '../../components/shared/PasswordInput';
import { adminApi } from '../../services/api';
import { validatePassword } from '../../utils/passwordValidation';

const SalesUsersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const gridRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ territory: '', role_id: '', organization_id: '', sales_team_id: '', is_active: 'true', search: '' });
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [salesTeams, setSalesTeams] = useState([]);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [alertStateSidePanel, setAlertStateSidePanel] = useState({ show: false, type: 'info', title: '', message: '' });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    role_id: '',
    organization_id: '',
    sales_team_id: '',
    territory: '',
    is_active: true
  });

  useEffect(() => {
    fetchRoles();
    fetchOrganizations();
    fetchSalesTeams();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  useEffect(() => {
    if (location.state?.mode === 'create') {
      setIsCreateMode(true);
      setSelectedUser(null);
      setShowPanel(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (userId && users.length > 0) {
      const user = users.find(u => u.id == userId);
      if (user) handleRowClick({ data: user });
    }
  }, [userId, users]);

  // Refetch roles and teams when organization changes in create mode
  useEffect(() => {
    if (isCreateMode && formData.organization_id) {
      fetchRolesForOrganization(formData.organization_id);
      fetchSalesTeamsForOrganization(formData.organization_id);
    } else if (isCreateMode && !formData.organization_id) {
      // Reset to all roles/teams when org is cleared
      fetchRoles();
      fetchSalesTeams();
    }
  }, [formData.organization_id, isCreateMode]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await adminApi.getSalesUsers(params);
      setUsers(res.data?.users || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load users', message: err.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      // Fetch both org_user and other_org_user categories using comma-separated values
      const res = await adminApi.getRoles({ category: 'org_user,other_org_user', is_active: 'true' });
      setRoles(res.data?.roles || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load roles', message: err.response?.data?.message || err.message });
      setRoles([]); // Ensure roles is always an array
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await adminApi.getOrganizations({ is_active: 'true' });
      setOrganizations(res.data?.organizations || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load organizations', message: err.response?.data?.message || err.message });
      setOrganizations([]);
    }
  };

  const fetchSalesTeams = async () => {
    try {
      const res = await adminApi.getSalesTeams({ is_active: 'true' });
      setSalesTeams(res.data?.teams || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load sales teams', message: err.response?.data?.message || err.message });
      setSalesTeams([]);
    }
  };

  const fetchRolesForOrganization = async (orgId) => {
    try {
      const res = await adminApi.getRoles({ category: 'org_user,other_org_user', is_active: 'true', organization: orgId });
      setRoles(res.data?.roles || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load roles', message: err.response?.data?.message || err.message });
      setRoles([]);
    }
  };

  const fetchSalesTeamsForOrganization = async (orgId) => {
    try {
      const res = await adminApi.getSalesTeams({ organization_id: orgId, is_active: 'true' });
      setSalesTeams(res.data?.teams || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load sales teams', message: err.response?.data?.message || err.message });
      setSalesTeams([]);
    }
  };

  const handleRowClick = useCallback((event) => {
    setIsCreateMode(false);
    setSelectedUser(event.data);
    setShowPanel(true);
    navigate(`/admin/sales/users/${event.data.id}`, { replace: true });
  }, [navigate]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedUser(null);
    setIsCreateMode(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      password: '',
      role_id: '',
      organization_id: '',
      sales_team_id: '',
      territory: '',
      is_active: true
    });
    navigate('/admin/sales/users', { replace: true });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setAlertStateSidePanel({ show: true, type: 'error', title: 'Validation Error', message: passwordValidation.error });
      return;
    }

    try {
      await adminApi.createSalesUser(formData);
      fetchUsers();
      handleClosePanel();
      setAlertState({ show: true, type: 'success', title: '', message: 'Sales user created successfully' });
    } catch (err) {
      setAlertStateSidePanel({ show: true, type: 'error', title: 'Failed to create user', message: err.response?.data?.message || err.message });
    }
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;
      await adminApi.updateSalesUser(data.id, { [colDef.field]: newValue });
      fetchUsers();
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to update', message: err.response?.data?.message || err.message });
      fetchUsers();
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({ fileName: `sales-users-${new Date().toISOString().split('T')[0]}.csv` });
  };

  const columnDefs = [
    { headerName: 'Name', valueGetter: p => `${p.data?.user?.first_name || ''} ${p.data?.user?.last_name || ''}`.trim() || '-', width: 150 },
    { headerName: 'Email', field: 'user.email', width: 200 },
    { headerName: 'Username', field: 'user.username', width: 130 },
    { headerName: 'Role', field: 'role.name', width: 100 },
    { headerName: 'Territory', field: 'territory', width: 120, editable: true },
    { headerName: 'Team', field: 'sales_team.name', width: 130 },
    { headerName: 'Manager', valueGetter: p => p.data?.manager?.user?.first_name ? `${p.data.manager.user.first_name} ${p.data.manager.user.last_name}` : '-', width: 130 },
    { headerName: 'Organizations', valueGetter: p => p.data?.organization_memberships?.map(m => m.organization?.name).join(', ') || '-', width: 180 },
    { headerName: 'Quota', field: 'quota_amount', width: 110 },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: p => (
        <span className={`badge bg-${p.data?.is_active ? 'success' : 'danger'}`}>
          {p.data?.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <>
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          dismissible={true}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
        <h4 className="text-lg">
          Sales Users
          <EducationalTooltip title="SDRs, AEs, and managers using the sales platform. Can belong to multiple organizations." />
        </h4>
        <p className="text-muted">Central resource for managing Sales Users for the Organization</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/sales">Sales</a></li>
              <li className="breadcrumb-item"><a href="/admin/sales/organizations">Organizations</a></li>
              <li className="breadcrumb-item active">Users</li>
            </ol>
          </nav>
        </div>
        </div>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <button
            className="btn btn-primary"
            onClick={() => navigate('/admin/sales/users', { state: { mode: 'create' } })}
          >
            <i className="fas fa-plus me-1" />Add User
          </button>
      </div>
      <div className='card'>
        <div className="d-flex d-flex-inline justify-content-between gap-2 p-2 mb-3">
          <div className='d-flex gap-2'>
            <select className="form-select form-select-sm" value={filters.role_id} onChange={e => setFilters({...filters, role_id: e.target.value})}>
              <option value="">All Roles</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select className="form-select form-select-sm" value={filters.sales_team_id} onChange={e => setFilters({...filters, sales_team_id: e.target.value})}>
              <option value="">All Teams</option>
              {salesTeams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
            <select className="form-select form-select-sm" value={filters.organization_id} onChange={e => setFilters({...filters, organization_id: e.target.value})}>
              <option value="">All Organizations</option>
              {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
            <select className="form-select form-select-sm" value={filters.is_active} onChange={e => setFilters({...filters, is_active: e.target.value})}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <input type="text" className="form-control form-control-sm" placeholder="Search..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
          </div>
          <div className='d-flex gap-2'>
            <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
              <i className="fas fa-book me-1" />Docs
            </button>
            <button className="btn btn-sm btn-success" onClick={handleExport}>
              <i className="fas fa-file-export me-1" />Export
            </button>
          </div>
        </div>
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : (
            <AgGridTable ref={gridRef} rowData={users} columnDefs={columnDefs} onRowClicked={handleRowClick} onCellValueChanged={handleCellValueChanged} />
          )}
      </div>

      <SidebarPanel
        isOpen={showPanel}
        onClose={handleClosePanel}
        title={isCreateMode ? "Create Sales User" : "Sales User Details"}
      >
        {alertStateSidePanel.show && (
          <AlertMessage
            type={alertStateSidePanel.type}
            title={alertStateSidePanel.title}
            message={alertStateSidePanel.message}
            dismissible={true}
            onDismiss={() => setAlertStateSidePanel({ ...alertStateSidePanel, show: false })}
            className="mb-3"
          />
        )}
        <div className="card">
          <div className="card-body p-3">
            {isCreateMode ? (
              <form onSubmit={handleCreateUser}>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">First Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      required
                      value={formData.first_name}
                      onChange={e => setFormData({...formData, first_name: e.target.value})}
                      placeholder="John"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Last Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      required
                      value={formData.last_name}
                      onChange={e => setFormData({...formData, last_name: e.target.value})}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Email *</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      required
                      value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                      placeholder="john.doe@example.com"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Username *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      required
                      value={formData.username}
                      onChange={e => setFormData({...formData, username: e.target.value})}
                      placeholder="john.doe@example.com"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <PasswordInput
                      value={formData.password}
                      onChange={e => setFormData({...formData, password: e.target.value})}
                      required
                      label="Password"
                      showRequirements
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Organization</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.organization_id}
                      onChange={e => setFormData({...formData, organization_id: e.target.value, sales_team_id: ''})}
                    >
                      <option value="">No Organization</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <small className="text-muted">Optional: Select organization to auto-create membership</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Role {formData.organization_id && '*'}</label>
                    <select
                      className="form-select form-select-sm"
                      required={!!formData.organization_id}
                      value={formData.role_id}
                      onChange={e => setFormData({...formData, role_id: e.target.value})}
                    >
                      <option value="">Select Role</option>
                      {roles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    {formData.organization_id && (
                      <small className="text-muted">Required when organization is selected</small>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Sales Team</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.sales_team_id}
                      onChange={e => setFormData({...formData, sales_team_id: e.target.value})}
                      disabled={!formData.organization_id}
                    >
                      <option value="">No Team Assigned</option>
                      {salesTeams
                        .filter(team => !formData.organization_id || team.organization?.id === formData.organization_id)
                        .map(team => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                    </select>
                    {!formData.organization_id ? (
                      <small className="text-muted">Select an organization first to see available teams</small>
                    ) : salesTeams.filter(team => team.organization?.id === formData.organization_id).length === 0 ? (
                      <small className="text-warning">No teams available for this organization</small>
                    ) : null}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Territory</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.territory}
                      onChange={e => setFormData({...formData, territory: e.target.value})}
                      placeholder="e.g., West Coast, EMEA, etc."
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.is_active}
                      onChange={e => setFormData({...formData, is_active: e.target.value === 'true'})}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <button type="submit" className="btn btn-primary me-2">
                      Create User
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClosePanel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            ) : selectedUser && (
              <>
                <SalesUserEditor
                  salesUser={selectedUser}
                  roles={roles}
                  salesTeams={salesTeams}
                  onSave={fetchUsers}
                  onAlert={(alert) => setAlertStateSidePanel({ show: true, ...alert })}
                />

              </>
            )}
          </div>
        </div>
      </SidebarPanel>

      <ModelDocumentationModal show={showDocsModal} onHide={() => setShowDocsModal(false)} modelType="org_user" />
    </>
  );
};

export default SalesUsersPage;
